import React from "react";
import VinesAppPreview from "@/components/VinesAppPreview";
import {
  Smartphone,
  Search,
  Star,
  TrendingUp,
  ArrowUpRight,
  ArrowRight,
} from "lucide-react";

// WineriesVinesSection — sells the "get listed on the wine-trail map app"
// pitch to a tasting-room operator. Lead question: "Are you easy to find?"
// Live map embed sits front and center so visitors can see all current
// listings before deciding to add their own.

const QUICK_BENEFITS = [
  { icon: Smartphone, t: "Mobile-first discovery" },
  { icon: TrendingUp, t: "Trail-stop traffic" },
  { icon: Search, t: "SEO that points to you" },
  { icon: Star, t: "Featured upgrades" },
];

const WineriesVinesSection = () => (
  <section
    className="relative px-5 sm:px-8 py-20 overflow-hidden border-y border-[var(--af-line)] bg-[var(--af-navy-deep)]"
    data-testid="wineries-vines-section"
  >
    {/* soft lime glow */}
    <div
      aria-hidden="true"
      className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full pointer-events-none"
      style={{
        background:
          "radial-gradient(closest-side, rgba(184,255,82,0.16), transparent 70%)",
      }}
    />

    <div className="relative max-w-7xl mx-auto">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--af-lime)]/40 bg-[var(--af-lime)]/10 mb-6">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
        <span className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-lime)]">
          Get on the Map · vines.app
        </span>
      </div>

      <div className="grid lg:grid-cols-12 gap-10 items-start">
        {/* LEFT: pitch + benefits + CTAs */}
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mb-5">
            Are you{" "}
            <span className="text-[var(--af-lime)]">easy to find?</span>
          </h2>
          <p className="text-lg text-[var(--af-cream)]/80 leading-relaxed mb-7">
            vines.app is the discovery engine 280+ Virginia wineries,
            breweries, cideries, and distilleries already use to reach
            travelers planning their next weekend. If you're not on the map
            they're using, you're not on the trip.
          </p>

          <div
            className="grid grid-cols-2 gap-x-5 gap-y-3 mb-8"
            data-testid="wineries-vines-quick-benefits"
          >
            {QUICK_BENEFITS.map((b, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-sm text-[var(--af-cream)]/85"
              >
                <b.icon size={14} className="text-[var(--af-lime)] flex-shrink-0" />
                <span>{b.t}</span>
              </div>
            ))}
          </div>

          <div
            className="rounded-2xl border border-[var(--af-lime)]/30 bg-[var(--af-lime)]/[0.06] p-5 mb-6"
            data-testid="wineries-vines-pricing-card"
          >
            <div className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-lime)] mb-3">
              Two ways to get on the map, both free
            </div>
            <ul className="space-y-2.5 mb-3">
              <li className="flex items-start gap-3">
                <span className="font-display text-[var(--af-lime)] text-base leading-tight mt-0.5">
                  ✓
                </span>
                <div>
                  <div className="font-display text-base text-[var(--af-cream)] leading-tight">
                    FREE listing on vines.app
                  </div>
                  <div className="text-xs text-[var(--af-cream)]/70 mt-0.5">
                    Your tasting room on the live trail map. Indexed, searchable, mobile-ready.
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-display text-[var(--af-lime)] text-base leading-tight mt-0.5">
                  ✓
                </span>
                <div>
                  <div className="font-display text-base text-[var(--af-cream)] leading-tight">
                    FREE map embed for your website
                  </div>
                  <div className="text-xs text-[var(--af-cream)]/70 mt-0.5">
                    Drop the live trail map onto your own site so visitors can plan their day-trip from your homepage.
                  </div>
                </div>
              </li>
            </ul>
            <div className="text-xs text-[var(--af-cream)]/65 pt-3 border-t border-[var(--af-lime)]/20">
              Featured-placement upgrades available for premium visibility.
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <a
              href="https://vines.app/join?utm_source=services.anneflinn.com&utm_medium=wineries-cta"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-lime"
              data-testid="wineries-vines-cta-join"
            >
              Get listed free <ArrowUpRight size={14} />
            </a>
            <a
              href="https://vines.app/embed?utm_source=services.anneflinn.com&utm_medium=wineries-cta"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost"
              data-testid="wineries-vines-cta-embed"
            >
              Get the embed <ArrowUpRight size={14} />
            </a>
            <a
              href="#lead-form"
              className="btn-ghost"
              data-testid="wineries-vines-cta-featured"
            >
              Talk featured placement <ArrowRight size={14} />
            </a>
          </div>
        </div>

        {/* RIGHT: live map */}
        <div className="lg:col-span-7" data-testid="wineries-vines-map-wrap">
          <VinesAppPreview hideIntro mapOnly className="px-0 py-0" />
        </div>
      </div>
    </div>
  </section>
);

export default WineriesVinesSection;
